import { taxonomyAttributeToFacetKey } from 'src/adapters/pageURLToStore/taxonomyPath';
import type { ReduxApplicationState } from 'src/redux/combinedReducer';
import type { Facet } from 'src/redux/productsQuery/types/Facet';
import type { TaxonomyAttribute } from 'src/redux/productsQuery/types/ProductsQueryState';
import { getListingsSortAsURLSearchParams } from './listings-api/getListingsSortAsURLSearchParams';
import { serializeFilterPath } from './serializeFilterPath';

type StoreToPageURLState = Pick<ReduxApplicationState, 'productsQuery'>;

/**
 * Strips facets whose `key` is registered as taxonomy in `productsQuery.taxonomyAttributes`.
 * Taxonomy lives in the pathname (e.g. `/shop/accessories/bags`) and must not also appear
 * in the `/-/...` filter segment. On routes that don't set `taxonomyAttributes` this is a no-op.
 */
const stripTaxonomyFacets = (
  facets: Facet[],
  taxonomyAttributes: TaxonomyAttribute[] | undefined,
): Facet[] => {
  if (!taxonomyAttributes?.length) {
    return facets;
  }
  const taxonomyKeys = new Set(
    taxonomyAttributes.map(({ attributeKey }) => taxonomyAttributeToFacetKey(attributeKey)),
  );
  return facets.filter((facet) => !taxonomyKeys.has(facet.key));
};

export const appendNonFacetListingParams = (
  urlParams: URLSearchParams,
  state: StoreToPageURLState,
  page?: number,
): void => {
  const {
    productsQuery: { searchTerm, sortOptions = [] },
  } = state;
  const activeSortOption = sortOptions.find((option) => option.active);
  if (activeSortOption) {
    getListingsSortAsURLSearchParams(urlParams, activeSortOption);
  }

  // don't include page=1 as that is the default
  if (page && page > 1) {
    urlParams.set('page', page.toString());
  }

  if (searchTerm) {
    urlParams.set('term', searchTerm);
  }
};

/**
 * Returns the filter path segment (e.g. "/-/p/-10/c/blue") for the active facets.
 * Returns an empty string when no filters are active.
 * Taxonomy facets (those keyed via `productsQuery.taxonomyAttributes`) are excluded — they live
 * in the pathname (e.g. `/shop/accessories/bags`), not the filter segment.
 */
export const adaptStoreToFilterPath = (state: StoreToPageURLState): string => {
  const { facets, taxonomyAttributes } = state.productsQuery;
  return serializeFilterPath(stripTaxonomyFacets(facets, taxonomyAttributes));
};

/**
 * Returns non-facet query params only (sort, page, term).
 * Facet filters are now serialised into the URL path via adaptStoreToFilterPath.
 */
export const adaptStoreToPageURL = (state: StoreToPageURLState, page?: number): URLSearchParams => {
  const urlParams = new URLSearchParams();
  appendNonFacetListingParams(urlParams, state, page);
  return urlParams;
};
